import { useEffect, useState, useContext, useRef } from 'react';
import { BannerContext } from '../../InternalApiApp';
import { Search } from 'react-feather';
import { formatRelative, parseISO, format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { PAGES, ROUTE_PATHS } from '../../InternalApiApp';
import { useStorage, useInfiniteScroll } from '../../Utils/Utils';
import { statuses } from './WorkflowDetails';
import PageGridItem from '../Shared/Page/PageGridItem';
import PageGridDivider from '../Shared/Page/PageGridDivider';
import Table from '../../components/Table/Table';
import TextInput from '../../components/Input/TextInput';
import DateRangeInput from '../CraigsLibraryPage/DateRangeInput';
import FlexWrapper from '../FlexWrapper';
import ProgressBar from '../../components/Input/ProgressBar';
import moment from 'moment';
import PageContentToggle from '../../components/Menus/PageContentToggle';
import Divider from '../../components/Divider';
import Button from '../../components/Buttons/Button';
import StartProcessModal from '../../components/Modals/StartProcessModal';
import Tooltip from '../../components/popups/Tooltip';
import LoadingIcon from '../../components/LoadingIcon';
import Checkbox from '../../components/Checkbox';


const headers = ["Template", "Coverage", "Last Update", "Progress"]

const WorkflowsList = ({ }) => {
	const { setBannerBreadCrumbs, setBannerAdditionalContent } = useContext(BannerContext)
	const [workflows, setWorkflows] = useState([]);
	const [completedWorkflows, setCompletedWorkflows] = useState([]);
	const [processFilter, setProcessFilter] = useState("");
	const [dateRange, setDateRange] = useState({ from: null, to: null });
	const [selectedTab, setSelectedTab] = useState('In Progress')
	const [loading, setLoading] = useState(true)
	const [showStartProcessModal, setShowStartProcessModal] = useState(false)
	const [moreWorkflowsToLoad, setMoreWorkflowsToLoad] = useState(true)
	const [filters, setFilters] = useState(['COMPLETE']);

	const history = useHistory()
	const storage = useStorage()

	const scrollableElementRef = useRef()
	const scrollableElementChildRef = useRef()
	const contentDivRef = useRef(null);

	const NUM_OF_DOCS_TO_LOAD = 20;
	const startProcessButton = <Button onClick={() => setShowStartProcessModal(true)} >Start Process</Button>


	useEffect(() => {
		setLoading(true)
		setBannerBreadCrumbs({ tab: 'Workflow Monitor' })
		setBannerAdditionalContent(startProcessButton)
		scrollToTop()

		storage.refresh(getQuery(true)).then(resp => {
			if (selectedTab === 'History') {
				const sortedData = resp.sort((a, b) => {
					const one = a.lastUpdatedAt
					const two = b.lastUpdatedAt
					return two.localeCompare(one)
				})
				setCompletedWorkflows(sortedData);
			} else setWorkflows(resp)
			setLoading(false)
		}).catch(() => {
			setLoading(false)
		});

		return () => setBannerAdditionalContent(null)
	}, [selectedTab, filters]);


	const loadMoreWorkflows = async () => {
		if (!moreWorkflowsToLoad) {
			return;
		}
		await storage.refresh(getQuery()).then(resp => {
			if (selectedTab === 'History') {
				setCompletedWorkflows(prevWorkflows => {
					const combinedData = [...prevWorkflows, ...resp];
					const sortedData = combinedData.sort((a, b) => {
						const one = a.lastUpdatedAt
						const two = b.lastUpdatedAt
						return two.localeCompare(one)
					})
					return sortedData;
				});
			} else {
				setWorkflows(prevWorkflows => [...prevWorkflows, ...resp]);
			}

			setMoreWorkflowsToLoad(resp.length === NUM_OF_DOCS_TO_LOAD)
			setLoading(false)
		})
	}

	const [isFetching,] = useInfiniteScroll(loadMoreWorkflows, scrollableElementChildRef, scrollableElementRef, true);

	function getQuery(initialLoad) {
		var base = `workflows/list?active=${selectedTab === 'History' ? false : true}&&num=${NUM_OF_DOCS_TO_LOAD}`
		const length = initialLoad ? 0 : selectedTab === 'History' ? completedWorkflows.length : workflows.length;
		const statusFilter = selectedTab === 'History' ? filters.map(f => `&status=${f}`).join('') : '';
		base += `&offset=${length}${statusFilter}`;
		return base;
	}

	function scrollToTop() {
		if (contentDivRef.current) {
			let element = contentDivRef.current
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition = elementPosition - 100;

			window.scrollBy({
				top: offsetPosition,
				behavior: "smooth"
			});
		}
	};

	function filterRows(workflow) {
		if (processFilter && !workflow.processName.toLowerCase().includes(processFilter.toLowerCase())) return false;
		if (dateRange.from || dateRange.to) {
			const lastUpdated = parseISO(workflow.lastUpdatedAt);
			if (
				(dateRange.from && moment(lastUpdated).isSameOrBefore(dateRange.from.format())) ||
				(dateRange.to && moment(lastUpdated).isSameOrAfter(dateRange.to.format()))
			) return false;
		}
		return true
	};

	function getRows(list) {
		if (!list || list.length === 0) return <></>;

		return list.filter(filterRows).map((workflow, idx) => {
			let workflowTaskProgress = 0
			let workflowTaskTotal = 0
			workflow?.tasks?.forEach((task, idx) => {
				if (statuses[task.status] !== statuses.SKIPPED) {
					workflowTaskTotal += 1
				}
				if (statuses[task.status] === statuses.COMPLETE) {
					workflowTaskProgress += 1
				}
			})
			const date = formatRelative(new Date(parseISO(workflow.lastUpdatedAt)), new Date())
			const arr = date.includes("at") ? date.split(" at ") : null
			const formattedDate = arr ? <p>
				<span style={{ textTransform: "capitalize" }}>{arr[0]}</span>
				<span>{" at " + arr[1]}</span>
			</p> : format(new Date(date), 'dd/MM/yyyy')
			return (
				<tr key={"workflow-" + workflow.processName + "-" + idx} style={{ cursor: "pointer" }} onClick={() => history.push(`${ROUTE_PATHS[PAGES.ACTIVE_WORKFLOWS]}/${workflow.id}`)} >
					<td>{workflow.templateName}</td>
					<td>
						<Tooltip label={workflow.coverageDetails?.map(coverage => <div style={{ lineHeight: 1.5 }}>{coverage}</div> || 'N/A')}>
							{workflow.processName}
						</Tooltip>
					</td>
					<td>{formattedDate}</td>
					<td>
						<FlexWrapper direction="column" style={{ maxWidth: "300px", paddingRight: "1rem", gap: "15px" }}>
							<ProgressBar noToolTip extent={workflowTaskTotal} value={workflowTaskProgress} />
						</FlexWrapper>
					</td>
				</tr>
			)
		})
	}

	return (
		<>
			{showStartProcessModal && <StartProcessModal
				cancelButton={{ onClick: () => setShowStartProcessModal(false) }}
				handleClose={() => setShowStartProcessModal(false)}
				header="Start Process"
				refreshList={() => {
					loadMoreWorkflows()
				}}
			/>}
			<PageGridItem colSpan='12' gap='xl' direction='col' style={{ height: "calc(100vh - 128px)", overflowY: "hidden" }} >
				<PageGridItem direction='row' gap='l' alignItems='center'>
					<PageContentToggle labels={[
						{ label: 'In Progress', active: selectedTab === 'In Progress', onClick: () => setSelectedTab('In Progress') },
						{ label: 'History', active: selectedTab === 'History', onClick: () => setSelectedTab('History') },
					]} />
					<Divider vertical height={32} />
					<TextInput
						placeholder="QFU, SIPO"
						value={processFilter}
						onChange={(e) => setProcessFilter(e.target.value)}
						icon={<Search />}
						size="small"
					/>
					<DateRangeInput
						onChange={(dateRange) => setDateRange(dateRange)}
						value={dateRange}
					/>
					{selectedTab === 'History' &&
						<div style={{ display: 'flex', gap: '0.5rem' }}>
							<Checkbox
								checked={filters.includes('COMPLETE')}
								label={<div style={{ fontSize: '0.9rem' }}>Complete</div>}
								onChange={() => setFilters(filters.includes('COMPLETE') ? filters.filter(f => f !== 'COMPLETE') : [...filters, 'COMPLETE'])}
							/>
							<Checkbox
								checked={filters.includes('CANCELLED')}
								label={<div style={{ fontSize: '0.9rem' }}>Cancelled</div>}
								onChange={() => setFilters(filters.includes('CANCELLED') ? filters.filter(f => f !== 'CANCELLED') : [...filters, 'CANCELLED'])}
							/>
						</div>
					}
				</PageGridItem>
				<PageGridDivider />
				<PageGridItem fullWidth showScrollbar forwardedRef={scrollableElementRef} style={{ height: "calc(100vh - 216px)", paddingTop: "10px", overflowY: "scroll", paddingBottom: "5rem" }} ref={contentDivRef}>
					<div id="table-wrapper-div" ref={scrollableElementChildRef}>
						<Table
							headers={headers}
							dataMap={getRows(selectedTab === 'History' ? completedWorkflows : workflows)}
							dataLoading={loading}
						/>
						{!moreWorkflowsToLoad
							? <p className='helper-text pt-s'>All Workflows Loaded</p>
							: isFetching && <div style={{ padding: "1rem 0", textAlign: "center" }}><LoadingIcon size={32} /></div>}
					</div>
				</PageGridItem>
			</PageGridItem>
		</>
	);
}

export default WorkflowsList;